import type { AiContext } from '@/services/ai.service';
import * as aiService from '@/services/ai.service';
import type * as Monaco from 'monaco-editor';

/* ================================================================== */
/*  AI-powered inline code suggestions for Monaco                     */
/* ================================================================== */

// All languages the inline completion provider supports.
// Solidity is primary; TS/JS/Python/Rust/Go support non-Web3 files.
const SUPPORTED_LANGUAGES = ['sol', 'typescript', 'javascript', 'python', 'rust', 'go'] as const;

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_MS = 600;
const MAX_CONTEXT_LINES = 60;

/**
 * Cache to avoid repeated API calls for the same context.
 * Key = hash of prefix context, Value = suggestion text.
 */
const suggestionCache = new Map<string, string>();
const MAX_CACHE_SIZE = 50;

function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    hash = (hash << 5) - hash + ch;
    hash |= 0;
  }
  return String(hash);
}

function addToCache(key: string, value: string) {
  if (suggestionCache.size >= MAX_CACHE_SIZE) {
    const firstKey = suggestionCache.keys().next().value;
    if (firstKey !== undefined) suggestionCache.delete(firstKey);
  }
  suggestionCache.set(key, value);
}

/**
 * Registers an inline completion provider for Solidity and common languages.
 * Pass `aiCtx` to include project-wide context (open files, projectId) in
 * the completion request — improves suggestion quality significantly.
 */
export function registerAiInlineCompletions(
  monaco: typeof Monaco,
  aiCtx: AiContext = {},
): Monaco.IDisposable {
  const disposables = SUPPORTED_LANGUAGES.map((lang) =>
    monaco.languages.registerInlineCompletionsProvider(
      lang as string,
      {
        provideInlineCompletions: async (
          model: Monaco.editor.ITextModel,
          position: Monaco.Position,
          _context: Monaco.languages.InlineCompletionContext,
          token: Monaco.CancellationToken,
        ): Promise<Monaco.languages.InlineCompletions> => {
          // Get prefix context (lines before cursor)
          const startLine = Math.max(1, position.lineNumber - MAX_CONTEXT_LINES);
          const prefix = model.getValueInRange({
            startLineNumber: startLine,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });

          // Don't trigger on empty lines or very short prefixes
          if (prefix.trim().length < 10) return { items: [] };

          // Get suffix context (lines after cursor)
          const endLine = Math.min(model.getLineCount(), position.lineNumber + 10);
          const suffix = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: position.column,
            endLineNumber: endLine,
            endColumn: model.getLineMaxColumn(endLine),
          });

          // Check cache
          const cacheKey = simpleHash(prefix.slice(-200));
          const cached = suggestionCache.get(cacheKey);
          if (cached) {
            return {
              items: [
                {
                  insertText: cached,
                  range: new monaco.Range(
                    position.lineNumber,
                    position.column,
                    position.lineNumber,
                    position.column,
                  ),
                },
              ],
            };
          }

          // Debounced API call
          return new Promise<Monaco.languages.InlineCompletions>((resolve) => {
            if (debounceTimer) clearTimeout(debounceTimer);

            debounceTimer = setTimeout(async () => {
              if (token.isCancellationRequested) {
                resolve({ items: [] });
                return;
              }

              try {
                const suggestion = await aiService.complete(
                  prefix.slice(-500),
                  suffix.slice(0, 200),
                  model.uri.path,
                  // Pass project context so the AI can reason about related files
                  {
                    ...aiCtx,
                    currentFile: model.uri.path,
                    currentCode: model.getValue().slice(0, 8000),
                  },
                );

                if (token.isCancellationRequested) {
                  resolve({ items: [] });
                  return;
                }

                if (!suggestion || suggestion.length < 2) {
                  resolve({ items: [] });
                  return;
                }

                // Clean up the suggestion - remove any markdown code fences
                let cleanSuggestion = suggestion;
                if (cleanSuggestion.startsWith('```')) {
                  const lines = cleanSuggestion.split('\n');
                  lines.shift(); // remove opening fence
                  if (lines[lines.length - 1]?.trim() === '```') lines.pop(); // remove closing fence
                  cleanSuggestion = lines.join('\n');
                }

                // Cache it
                addToCache(cacheKey, cleanSuggestion);

                resolve({
                  items: [
                    {
                      insertText: cleanSuggestion,
                      range: new monaco.Range(
                        position.lineNumber,
                        position.column,
                        position.lineNumber,
                        position.column,
                      ),
                    },
                  ],
                });
              } catch {
                resolve({ items: [] });
              }
            }, DEBOUNCE_MS);
          });
        },

        freeInlineCompletions: () => {
          // No resources to free
        },
      } as unknown as Monaco.languages.InlineCompletionsProvider,
    ),
  );

  // Return a single disposable that cleans up all language registrations
  return {
    dispose: () => disposables.forEach((d) => d.dispose()),
  };
}

/**
 * Registers code actions provider for Solidity that offers AI-powered quick fixes.
 */
export function registerAiCodeActions(monaco: typeof Monaco): Monaco.IDisposable {
  return monaco.languages.registerCodeActionProvider('sol', {
    provideCodeActions: (
      model: Monaco.editor.ITextModel,
      range: Monaco.Range,
      _context: Monaco.languages.CodeActionContext,
      _token: Monaco.CancellationToken,
    ): Monaco.languages.CodeActionList => {
      const selectedText = model.getValueInRange(range);
      if (!selectedText.trim()) return { actions: [], dispose: () => {} };

      const actions: Monaco.languages.CodeAction[] = [
        {
          title: '🤖 AI: Explain this code',
          kind: 'quickfix',
          diagnostics: [],
          isPreferred: false,
        },
        {
          title: '🛡️ AI: Security check',
          kind: 'quickfix',
          diagnostics: [],
          isPreferred: false,
        },
        {
          title: '⚡ AI: Optimize gas',
          kind: 'quickfix',
          diagnostics: [],
          isPreferred: false,
        },
      ];

      return { actions, dispose: () => {} };
    },
  });
}
